import { useEffect, useState, useMemo } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, ChevronDown, Headphones } from "lucide-react";
import { useSettings } from "@/lib/settings";
import { LOGO_URL } from "@/lib/brand";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

function buildTree(items) {
  const byId = {};
  items.forEach((i) => { byId[i.id] = { ...i, children: [] }; });
  const roots = [];
  items.forEach((i) => {
    if (i.parent_id && byId[i.parent_id]) byId[i.parent_id].children.push(byId[i.id]);
    else roots.push(byId[i.id]);
  });
  const sortByOrder = (a, b) => (a.order || 0) - (b.order || 0);
  roots.sort(sortByOrder);
  roots.forEach((r) => r.children.sort(sortByOrder));
  return roots;
}

export default function Navbar() {
  const { settings, menu } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const tree = useMemo(() => {
    if (!menu || menu.length === 0) {
      return [
        { id: "h", label: "Home", link: "/", children: [] },
        { id: "p", label: "Products", link: "/products", children: [] },
        { id: "c", label: "Contact", link: "/contact", children: [] },
      ];
    }
    return buildTree(menu);
  }, [menu]);

  const logoSrc = settings?.logo_url || LOGO_URL;
  const phone = settings?.phone || "+91 70 9163 9163";

  // Always scroll to top when the logo (or any nav link to "/") is clicked.
  // Also handles the "already on /" case where React Router would otherwise do nothing.
  const goHome = (e) => {
    if (loc.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Schedule a scroll-to-top after navigation completes.
      setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" }), 50);
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#1A1A1A]/95 backdrop-blur border-b border-[#E65C00]/30" : "bg-[#1A1A1A]"
      }`}
      data-testid="site-navbar"
    >
      {/* Top utility bar */}
      <div className="hidden md:block bg-black/40 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between text-xs text-gray-300">
          <span className="tracking-widest uppercase">CIN: {settings?.cin || "U50400BR2016PTC031403"}</span>
          <span className="text-[#FFCC00] uppercase tracking-[0.3em]">Buy · Sell · Rent</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
        <Link to="/" onClick={goHome} className="flex items-center" data-testid="navbar-logo" aria-label="Tara Autohub — Home">
          <img src={logoSrc} alt="Tara Autohub" className="h-14 w-14 object-contain shrink-0" />
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5">
          {tree.map((it) => {
            const hasChildren = it.children && it.children.length > 0;
            // Hover-only parent (link === "#") must NOT be treated as an active
            // route by NavLink — otherwise react-router marks it active on
            // every page and it renders permanently yellow. Render a span.
            const isHoverOnly = !it.link || it.link === "#";
            if (!hasChildren) {
              if (isHoverOnly) {
                return (
                  <span
                    key={it.id}
                    className="px-2.5 py-2 text-sm font-semibold uppercase tracking-wide text-gray-200 hover:text-white transition-colors cursor-default"
                    data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {it.label}
                  </span>
                );
              }
              return (
                <NavLink
                  key={it.id}
                  to={it.link}
                  end={it.link === "/"}
                  className={({ isActive }) =>
                    `px-2.5 py-2 text-sm font-semibold uppercase tracking-wide transition-colors ${
                      isActive ? "text-[#FFCC00]" : "text-gray-200 hover:text-white"
                    }`
                  }
                  data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {it.label}
                </NavLink>
              );
            }
            return (
              <div key={it.id} className="relative group" data-testid={`nav-dropdown-${it.label.toLowerCase()}`}>
                {isHoverOnly ? (
                  <span
                    className="px-2.5 py-2 text-sm font-semibold uppercase tracking-wide flex items-center gap-1 text-gray-200 group-hover:text-white transition-colors cursor-default"
                  >
                    {it.label} <ChevronDown className="w-3 h-3 group-hover:rotate-180 transition-transform" />
                  </span>
                ) : (
                  <NavLink
                    to={it.link}
                    end={it.link === "/"}
                    className={({ isActive }) =>
                      `px-2.5 py-2 text-sm font-semibold uppercase tracking-wide flex items-center gap-1 transition-colors ${
                        isActive ? "text-[#FFCC00]" : "text-gray-200 hover:text-white"
                      }`
                    }
                  >
                    {it.label} <ChevronDown className="w-3 h-3 group-hover:rotate-180 transition-transform" />
                  </NavLink>
                )}
                <div className="absolute left-0 top-full pt-2 min-w-[240px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150">
                  <div className="bg-[#1A1A1A] border border-[#E65C00]/40 shadow-2xl py-2">
                    {it.children.map((c) => (
                      <Link
                        key={c.id}
                        to={c.link || "#"}
                        className="block px-5 py-2.5 text-sm font-semibold text-gray-300 hover:text-[#FFCC00] hover:bg-white/5 transition-colors border-l-2 border-transparent hover:border-[#E65C00]"
                        data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          {/* Call Support button — styled like a call-centre line */}
          <a
            href={`tel:${phone.replace(/\s+/g, "")}`}
            className="hidden md:flex items-center gap-3 bg-[#E65C00] hover:bg-[#CC5200] text-white px-4 py-2.5 transition-colors group"
            data-testid="navbar-call-support"
          >
            <div className="w-9 h-9 bg-[#1A1A1A] flex items-center justify-center group-hover:bg-black transition-colors">
              <Headphones className="w-4 h-4 text-[#FFCC00]" />
            </div>
            <div className="text-left leading-tight whitespace-nowrap">
              <div className="text-[9px] uppercase tracking-[0.25em] text-[#FFCC00] font-bold">CALL SUPPORT</div>
              <div className="font-heading font-black text-sm tracking-tight whitespace-nowrap">{phone}</div>
            </div>
          </a>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden text-white p-2" data-testid="navbar-mobile-toggle">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#1A1A1A] border-l border-[#E65C00]/30 text-white w-[85%] overflow-y-auto">
              <div className="flex items-center gap-3 mt-2">
                <img src={logoSrc} alt="" className="h-12 w-12 object-contain" />
              </div>
              <div className="mt-8 flex flex-col gap-1">
                {tree.map((it) => {
                  const hasChildren = it.children && it.children.length > 0;
                  const isHoverOnly = !it.link || it.link === "#";
                  return (
                    <div key={it.id}>
                      <div className="flex">
                        {isHoverOnly ? (
                          <button
                            type="button"
                            onClick={() => setMobileExpanded((s) => ({ ...s, [it.id]: !s[it.id] }))}
                            className="flex-1 text-left py-3 px-4 font-heading font-bold uppercase tracking-wider border-l-4 border-transparent text-gray-200"
                            data-testid={`mobile-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {it.label}
                          </button>
                        ) : (
                          <NavLink
                            to={it.link}
                            end={it.link === "/"}
                            className={({ isActive }) =>
                              `flex-1 py-3 px-4 font-heading font-bold uppercase tracking-wider border-l-4 ${
                                isActive ? "border-[#E65C00] text-[#FFCC00] bg-white/5" : "border-transparent text-gray-200"
                              }`
                            }
                            data-testid={`mobile-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {it.label}
                          </NavLink>
                        )}
                        {hasChildren && (
                          <button
                            onClick={() => setMobileExpanded((s) => ({ ...s, [it.id]: !s[it.id] }))}
                            className="px-4 text-gray-400 hover:text-[#FFCC00]"
                            aria-label="Expand submenu"
                          >
                            <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded[it.id] ? "rotate-180" : ""}`} />
                          </button>
                        )}
                      </div>
                      {hasChildren && mobileExpanded[it.id] && (
                        <div className="ml-4 border-l border-[#E65C00]/30">
                          {it.children.map((c) => (
                            <Link key={c.id} to={c.link || "#"} className="block py-2.5 px-4 text-sm text-gray-400 hover:text-[#FFCC00]">{c.label}</Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <a href={`tel:${phone.replace(/\s+/g, "")}`} className="mt-6 flex items-center gap-3 bg-[#E65C00] hover:bg-[#CC5200] text-white px-4 py-3" data-testid="mobile-call-support">
                  <Phone className="w-5 h-5" />
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-[#FFCC00] font-bold">CALL SUPPORT</div>
                    <div className="font-heading font-black">{phone}</div>
                  </div>
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
